// src/contexts/CartContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getCartData,
  addSparePartInCart,
  updateSparePartCart,
  deleteSparePartInCart,
  getSpareParts,
  getUserDetails,
} from "../Services/authService";
import useAuth from "../Hooks/useAuth";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);      // array of cart items from backend
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const loadCart = async () => {
    try {
      setLoading(true);
      const data = await getCartData();

      // Enrich cart items with availableQuantity from spare parts
      if (data && data.length > 0) {
        try {
          const sparePartsData = await getSpareParts('', '', '');

          const enrichedCart = data.map(cartItem => {
            const sparePart = sparePartsData.find(sp => sp.id === cartItem.partId);
            return {
              ...cartItem,
              availableQuantity: sparePart ? sparePart.availableQuantity : 0
            };
          });

          setCart(enrichedCart);
        } catch (err) {
          console.error("Error enriching cart with availableQuantity", err);
          setCart(data || []);
        }
      } else {
        setCart(data || []);
      }
    } catch (err) {
      console.error("loadCart error", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!token) return;

      const user = await getUserDetails();   // <-- valid async call
      // console.log(user, "hhhhhhhhh");

      // Super Admin should not load cart
      if (user?.franchiseId === 1) {
        // console.log("Skipping cart load for Super Admin");
        return;
      }

      loadCart();   // safe to call
    };

    init();
  }, [token]);

  const addItem = async (partId, quantity = 1) => {
    try {
      setLoading(true);
      // API expects payload like { itemId, quantity } — adjust if required
      await addSparePartInCart({ partId, quantity });
      await loadCart();
      return true;
    } catch (err) {
      console.error("addItem error", err);
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateItemQty = async (itemId, quantity) => {
    try {
      setLoading(true);
      await updateSparePartCart(itemId, quantity);
      await loadCart();
      return true;
    } catch (err) {
      console.error("updateItemQty error", err);
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      setLoading(true);
      await deleteSparePartInCart(itemId);
      await loadCart();
      return true;
    } catch (err) {
      console.error("removeItem error", err);
      setError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Helper to get quantity in cart for a specific part
  const getCartQuantityForPart = (partId) => {
    const cartItem = cart.find(item => item.partId === partId || item.id === partId);
    return cartItem ? cartItem.quantity : 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        error,
        loadCart,
        addItem,
        updateItemQty,
        removeItem,
        getCartQuantityForPart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Hook to use cart
export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
};
